import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackEvent } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { Button, FlyInPanel, Menu, MenuItem, Text } from '../../providers/harmony-scope/lazy-enablers';
import { EUserNotificationStatus } from '../../ducks/api-ducks/user-notifications-duck/types-user-notifications';
import DropdownFilterPill from '../../shared-components/dropdown-filter-pill';
import UserNotificationsTable from './user-notifications-table';
import {
  useGetUserNotificationsQuery,
  useLazyGetUserNotificationsQuery,
} from '../../ducks/api-ducks/user-notifications-duck/api-duck-user-notifications';
import { useAppSelector } from '../../ducks/hooks';

interface IProps {
  closePanel: () => void;
}

const componentName = 'Notifications Panel';

export const NotificationsPanel = (props: IProps) => {
  // Hooks
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackEvent = useTrackEvent();

  /* Note: isPanelOpen will always be true since control of the open state is actually in its parent
   **       The state of "opening" here is to control the transition from closed to open since otherwise it would flick open.
   */
  const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
  // Open panel gracefully
  useEffect(() => {
    setPanelOpen(true);
  }, []);
  const { closePanel: parentClosePanel } = props;
  // delay to call parent's close panel to allow css transition to play. lookup info on javascript event loop for more info.
  const closePanel = useCallback(() => {
    setTimeout(parentClosePanel, 0);
  }, [parentClosePanel]);
  const handlePanelClosing = useCallback(() => {
    trackEvent('Notifications panel closed', pageName, componentName);
    closePanel();
  }, [closePanel, pageName, trackEvent]);

  // global state accessors
  const impersonation = useAppSelector((state) => state.impersonation);

  // API clients
  const {
    data: activeUserNotificationsData,
    isFetching: activeUserNotificationsIsFetching,
    isError: activeUserNotificationsIsError,
    // error: userNotificationsError,
  } = useGetUserNotificationsQuery({
    cacheKey: impersonation.impersonatedUser, // Caching key to re-fetch after different impersonations.
    notificationStatus: EUserNotificationStatus.Active, // We first fetch only active notifications, then expired ones on demand.
  });

  const [
    fetchExpiredNotifications,
    { data: expiredUserNotificationsData, isFetching: expiredUserNotificationsIsFetching, isError: expiredUserNotificationsIsError },
  ] = useLazyGetUserNotificationsQuery();

  // component state
  const [selectedNotificationStatus, setSelectedNotificationStatus] = useState({
    [EUserNotificationStatus.Active]: true,
    [EUserNotificationStatus.Expired]: false,
  });
  const sortedNotifications = useMemo(() => {
    const newNotifications = [...(activeUserNotificationsData?.notifications || []), ...(expiredUserNotificationsData?.notifications || [])];
    return (
      newNotifications
        .filter((notification) => {
          const notificationStatus = notification.notificationStatus || '';
          return selectedNotificationStatus[notificationStatus as EUserNotificationStatus];
        })
        .sort((a, b) => {
          const dateA = new Date(a.notificationStartDate || 0);
          const dateB = new Date(b.notificationStartDate || 0);
          return dateB.getTime() - dateA.getTime();
        }) || []
    );
  }, [activeUserNotificationsData?.notifications, expiredUserNotificationsData?.notifications, selectedNotificationStatus]);

  const dropdownOptions = useMemo(
    () => Object.keys(selectedNotificationStatus).filter((key) => selectedNotificationStatus[key as EUserNotificationStatus]),
    [selectedNotificationStatus]
  );

  // Effects
  // Track event when the panel opens
  useEffect(() => {
    if (isPanelOpen) {
      trackEvent('Notifications panel opened', pageName, componentName);
    }
  }, [isPanelOpen, trackEvent, pageName]);

  // Fetch expired notifications when the user selects the expired status
  useEffect(() => {
    if (selectedNotificationStatus[EUserNotificationStatus.Expired]) {
      void fetchExpiredNotifications(
        {
          cacheKey: impersonation.impersonatedUser,
          notificationStatus: EUserNotificationStatus.Expired,
        },
        true
      ); // second param defines preferCached, so that we don't retrigger if we already have cached values.
    }
  }, [selectedNotificationStatus, fetchExpiredNotifications, impersonation.impersonatedUser]);

  // Handlers
  const handleNotificationStatusSelectionChange = useCallback(
    ({ target }: CustomEvent<unknown>) =>
      setSelectedNotificationStatus((currentSelectedNotificationStatus) => {
        let newNotificationStatus = { ...currentSelectedNotificationStatus };
        if ((target as HTMLElement)?.textContent !== undefined) {
          newNotificationStatus = {
            ...currentSelectedNotificationStatus,
            [(target as HTMLInputElement).textContent || '']: (target as HTMLInputElement)?.checked,
          };
        }
        // if all are false, return previous state
        if (Object.values(newNotificationStatus).every((status) => status === false)) {
          return { ...currentSelectedNotificationStatus };
        }
        return newNotificationStatus;
      }),
    []
  );

  return (
    <FlyInPanel
      heading={t('notificationsPanelTitle')}
      style={{ '--size': 'var(--he-panel-size-medium)', zIndex: 3 }}
      open={isPanelOpen}
      onHeFlyInAfterHide={handlePanelClosing}
      slot="panel"
      contained
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text appearance="label" tag="span">
          {`${t('notificationsPanelFitleredBy')}:`}
        </Text>
        <DropdownFilterPill label="Status:" values={dropdownOptions}>
          <Menu>
            {Object.keys(selectedNotificationStatus).map((key) => (
              <MenuItem
                key={key}
                role="menuitemcheckbox"
                checked={selectedNotificationStatus[key as EUserNotificationStatus]}
                onHeChange={handleNotificationStatusSelectionChange}
              >
                {key}
              </MenuItem>
            ))}
          </Menu>
        </DropdownFilterPill>
      </div>
      {!activeUserNotificationsIsError && !expiredUserNotificationsIsError && sortedNotifications && (
        <UserNotificationsTable
          notifications={sortedNotifications}
          isLoading={activeUserNotificationsIsFetching || expiredUserNotificationsIsFetching}
        />
      )}
      <Button slot="footer" appearance="secondary" onClick={() => setPanelOpen(false)}>
        {t('notificationsPanelClose')}
      </Button>
    </FlyInPanel>
  );
};
